/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import {
  Checkbox,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import React from 'react';
import { useCoreTranslation } from '../../../../../utils/i18n';

interface SelectableFeaturesProps {
  layerId: string;
  isSelectable: boolean;
  onToggleSelectable: () => void;
  disabled?: boolean;
}

const SelectableFeatures: React.FC<SelectableFeaturesProps> = ({
  layerId,
  isSelectable,
  onToggleSelectable,
  disabled = false,
}: SelectableFeaturesProps) => {
  const { t } = useCoreTranslation();
  const onClickCheckbox = (): void => {
    if (disabled) {
      return;
    }
    onToggleSelectable();
  };

  return (
    <ListItem
      disablePadding
      sx={{
        width: '100%',
        height: '100%',
        borderBottom: '1px solid #E0E0E0',
      }}
    >
      <ListItemButton
        data-testid={`selectablefeatures-checkbox-${layerId}`}
        disableGutters
        disabled={disabled}
        onClick={() => onClickCheckbox()}
        role={undefined}
        sx={{
          paddingX: '16px',
          paddingY: '6px',
          height: '100%',
        }}
      >
        <ListItemText
          id={`selectablefeatures-checkbox-${layerId}`}
          primary={
            <Typography
              sx={{
                float: 'left',
                fontSize: '16px',
              }}
            >
              {t('layermanager-selectable-features')}
            </Typography>
          }
        />
        <ListItemIcon sx={{ width: '42px' }}>
          <Checkbox
            checked={isSelectable}
            disableRipple
            disabled={disabled}
            name={`selectablefeatures-checkbox-${layerId}`}
            tabIndex={-1}
          />
        </ListItemIcon>
      </ListItemButton>
    </ListItem>
  );
};

export default SelectableFeatures;
